import React, { useRef } from 'react'
import {ChevronLeft, ChevronRight} from 'lucide-react'
import ProductCardV2 from '../../shop/v2/ProductCardV2'
import type { ProductV2 } from '../../shop/v2/types'
import type { Lang } from './types'

interface RelatedProductsCarouselV2Props {
  products: ProductV2[]
  currentLang: Lang
}

/**
 * RelatedProductsCarouselV2
 *
 * Horizontal scroll carousel "Other Products" — Barista Map style.
 * Reuses ProductCardV2 from the shop v2 module so visual language stays
 * consistent across the V2 storefront.
 */
const RelatedProductsCarouselV2: React.FC<RelatedProductsCarouselV2Props> = ({
  products,
  currentLang,
}) => {
  const isZh = currentLang === 'zh'
  const scrollerRef = useRef<HTMLDivElement>(null)

  if (!products || products.length === 0) return null

  const scrollBy = (dir: 1 | -1) => {
    const el = scrollerRef.current
    if (!el) return
    const card = el.querySelector<HTMLElement>('[data-related-card]')
    const step = card ? card.offsetWidth + 16 : 320
    el.scrollBy({ left: dir * step * 2, behavior: 'smooth' })
  }

  return (
    <section className="border-t border-black/10 bg-[#FAF9F1]">
      <div className="mx-auto max-w-[1600px] px-6 py-16 md:py-24 md:px-10">
        <div className="mb-10 flex items-end justify-between">
          <h2
            className="text-2xl md:text-3xl text-black"
            style={{
              fontFamily: "'EB Garamond', 'Noto Serif TC', serif",
              fontWeight: 400,
              letterSpacing: '0.02em',
            }}
          >
            {isZh ? '其他商品' : 'Other Products'}
          </h2>

          <div className="hidden items-center gap-2 md:flex">
            <button
              onClick={() => scrollBy(-1)}
              className="flex h-10 w-10 items-center justify-center border border-black/15 text-black/70 hover:bg-black hover:text-white transition-colors"
              aria-label="Scroll left"
            >
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>
            <button
              onClick={() => scrollBy(1)}
              className="flex h-10 w-10 items-center justify-center border border-black/15 text-black/70 hover:bg-black hover:text-white transition-colors"
              aria-label="Scroll right"
            >
              <ChevronRight size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto pb-4 md:gap-6"
          style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none' }}
        >
          {products.map((product) => (
            <div
              key={product._id}
              data-related-card
              className="w-[70vw] flex-shrink-0 sm:w-[44vw] md:w-[300px] lg:w-[320px]"
              style={{ scrollSnapAlign: 'start' }}
            >
              <ProductCardV2 product={product} currentLang={currentLang} />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default RelatedProductsCarouselV2
